import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';
import {
    ScmIntegrationsApi,
    scmIntegrationsApiRef,
    ScmAuth,
} from '@backstage/integration-react';
import {
    AnyApiFactory,
    configApiRef,
    createApiFactory,
    discoveryApiRef,
    oauthRequestApiRef,
    OAuthApi,
    useApi,
    ConfigApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
    graphQlBrowseApiRef,
    type GraphQLEndpoint,
} from '@backstage-community/plugin-graphiql';
import { createApiRef, ProfileInfoApi, BackstageIdentityApi, SessionApi } from '@backstage/core-plugin-api';

import { ApiEntity } from '@backstage/catalog-model';
import {
    ApiDefinitionWidget,
    ApiDocsConfig,
    apiDocsConfigRef,
    defaultDefinitionWidgets,
} from '@backstage/plugin-api-docs';
import type { ComponentProps } from 'react';
import { useEffect, useRef } from 'react';
type SwaggerUIProps = ComponentProps<typeof SwaggerUI>;

import { OAuth2 } from '@backstage/core-app-api';

// Platform token holder for OIDC redirect-based auth
// The OIDC access_token is stored here by PlatformTokenSignInPage after
// completing the PKCE redirect flow, and returned by getAccessToken() for API calls.
// When the token expires, we re-trigger the OIDC redirect flow to get a fresh token.
let _platformToken: string | null = null;
let _tokenExpiry: number = 0; // epoch ms
let _refreshInProgress = false;

export function setPlatformToken(token: string) {
    _platformToken = token;
    // Decode JWT to extract expiry
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        _tokenExpiry = (payload.exp || 0) * 1000;
        console.log('[Platform Auth] Token set. Expires:', new Date(_tokenExpiry).toISOString(), '(refresh will trigger 60s before)');
    } catch {
        _tokenExpiry = 0;
    }
}

export function getPlatformToken(): string | null {
    return _platformToken;
}

function isTokenExpired(): boolean {
    if (!_tokenExpiry) return false;
    // Consider expired 60 seconds before actual expiry
    return Date.now() > _tokenExpiry - 60_000;
}

/**
 * Re-trigger the OIDC redirect flow to get a fresh token.
 * Since the user already has an active session with Platform Authentication,
 * the redirect is transparent (no login form shown).
 */
function triggerReauthRedirect(configApi: ConfigApi): void {
    if (_refreshInProgress) return;
    _refreshInProgress = true;
    console.log('[Platform Auth] Token expired — triggering OIDC redirect for fresh token. Expiry was:', new Date(_tokenExpiry).toISOString());

    const clientId = configApi.getOptionalString('app.platformAuth.clientId');
    const metadataUrl = configApi.getOptionalString('app.platformAuth.metadataUrl');
    if (!clientId || !metadataUrl) return;

    const authority = metadataUrl.replace('/.well-known/openid-configuration', '');

    // Generate CSRF state
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    const state = Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
    sessionStorage.setItem('platform_oidc_state', state);

    const authorizeUrl = new URL(`${authority}/authorize`);
    authorizeUrl.searchParams.set('client_id', clientId);
    authorizeUrl.searchParams.set('redirect_uri', window.location.origin);
    authorizeUrl.searchParams.set('response_type', 'code');
    authorizeUrl.searchParams.set('scope', 'openid profile email');
    authorizeUrl.searchParams.set('state', state);
    authorizeUrl.searchParams.set('prompt', 'none'); // silent — no login UI

    window.location.href = authorizeUrl.toString();
}

// Platform custom auth ref - provides getAccessToken() for API calls
// In production (iframe or direct), uses OIDC PKCE redirect flow for auth
// On localhost, falls back to OAuth2 popup flow for dev convenience
export const platformAuthApiRef = createApiRef
    <OAuthApi & ProfileInfoApi & BackstageIdentityApi & SessionApi>
    ({ id: 'auth.oidc' });


function ProxiedSwaggerUi(props: SwaggerUIProps) {
    const discoveryApi = useApi(discoveryApiRef);
    const platformAuth = useApi(platformAuthApiRef);
    const configApi = useApi(configApiRef);

    // Get the actual API URL for curl command display
    // Auto-detects from app.baseUrl environment
    // Examples:
    //   https://developers.causeway.com -> https://api.causeway.com
    //   https://developers.causeway-dev.com -> https://api.causeway-dev.com
    //   https://developers.causeway-staging.com -> https://api.causeway-staging.com
    //   http://localhost:3000 -> https://api.causeway-dev.com
    const appBaseUrl = configApi.getOptionalString('app.baseUrl') || '';
    const envMatch = appBaseUrl.match(/causeway(-[a-z]+)?\.com/);

    let actualApiUrl: string;
    if (appBaseUrl.includes('localhost') || appBaseUrl.includes('127.0.0.1')) {
        // Localhost - use dev API
        actualApiUrl = 'https://api.causeway-dev.com';
    } else if (envMatch && envMatch[1]) {
        // Environment suffix detected (e.g., -dev, -staging, -qa)
        actualApiUrl = `https://api.causeway${envMatch[1]}.com`;
    } else {
        // Production (no environment suffix)
        actualApiUrl = 'https://api.causeway.com';
    }

    // Modify the spec to set the correct server URL for curl commands
    let modifiedSpec = props.spec;

    // Handle both string and object specs
    if (typeof modifiedSpec === 'string') {
        try {
            modifiedSpec = JSON.parse(modifiedSpec);
        } catch (e) {
            console.warn('Could not parse spec string', e);
        }
    }

    if (typeof modifiedSpec === 'object' && modifiedSpec !== null) {
        // Deep clone the spec to avoid mutation issues
        modifiedSpec = JSON.parse(JSON.stringify(modifiedSpec));

        const spec = modifiedSpec as any;

        // Extract basePath from original server URL if it exists
        let originalBasePath = '';
        if (spec.servers && spec.servers.length > 0) {
            try {
                const originalServerUrl = new URL(spec.servers[0].url);
                originalBasePath = originalServerUrl.pathname;
            } catch (e) {
                // Not a valid URL, might be a relative path
            }
        }

        // Check if this is OpenAPI 3.0 or Swagger 2.0
        if (spec.openapi && spec.openapi.startsWith('3')) {
            // OpenAPI 3.0 - use servers
            const fullUrl = originalBasePath ? `${actualApiUrl}${originalBasePath}` : actualApiUrl;
            spec.servers = [
                {
                    url: fullUrl,
                    description: 'CausewayOne API',
                },
            ];
        } else if (spec.swagger && spec.swagger.startsWith('2')) {
            // Swagger 2.0 - use host, basePath, and schemes
            const url = new URL(actualApiUrl);
            spec.host = url.host;
            spec.basePath = originalBasePath || url.pathname.replace(/\/$/, '') || '/';
            spec.schemes = [url.protocol.replace(':', '')];
        }

    }

    // Store original fetch
    const originalFetchRef = useRef<typeof fetch>();

    // Set up fetch interception when component mounts
    useEffect(() => {
        // Save original fetch - bind it to window to preserve context
        originalFetchRef.current = window.fetch.bind(window);

        // Create custom fetch that handles proxying
        const customFetch: typeof fetch = async (input, init?) => {
            const url = typeof input === 'string' ? input : input instanceof Request ? input.url : input.toString();

            // Only process absolute URLs that might need proxying
            if (typeof url === 'string' && url.startsWith('http')) {
                try {
                    const urlObj = new URL(url);
                    const actualUrlObj = new URL(actualApiUrl);

                    if (urlObj.origin === actualUrlObj.origin) {
                        // This is a request to the actual API, route it through our custom proxy
                        // that forwards ALL headers automatically
                        const proxyUrl = await discoveryApi.getBaseUrl('api-proxy');
                        const proxyUrlObj = new URL(proxyUrl);

                        // Build the proxied URL - api-proxy strips its prefix automatically
                        const finalUrl = `${proxyUrlObj.origin}${proxyUrlObj.pathname}${urlObj.pathname}${urlObj.search}`;

                        // Get access token and add to headers (if not already present from requestInterceptor)
                        const headers = new Headers(init?.headers);
                        if (!headers.has('Authorization')) {
                            try {
                                const accessToken = await platformAuth.getAccessToken([
                                    'openid',
                                    'profile',
                                    'email',
                                ]);
                                if (accessToken) {
                                    headers.set('Authorization', `Bearer ${accessToken}`);
                                }
                            } catch (e) {
                                console.error('Failed to get access token', e);
                            }
                        }

                        // Call original fetch with proxied URL
                        return originalFetchRef.current!(finalUrl, { ...init, headers });
                    }
                } catch (e) {
                    // Silently ignore URL parsing errors for non-matching URLs
                }
            }

            // For non-API URLs or relative URLs, use original fetch
            return originalFetchRef.current!(input, init);
        };

        // Replace global fetch
        window.fetch = customFetch as any;

        // Cleanup: restore original fetch when component unmounts
        return () => {
            if (originalFetchRef.current) {
                window.fetch = originalFetchRef.current;
            }
        };
    }, [actualApiUrl, discoveryApi, platformAuth]);

    // Fix button alignment in Swagger UI authorization modal
    useEffect(() => {
        const fixButtonAlignment = () => {
            const forms = document.querySelectorAll('.swagger-ui .auth-container form');

            forms.forEach(form => {
                // Check if already fixed
                if (form.hasAttribute('data-buttons-fixed')) return;

                const buttonWrapper = form.querySelector('.auth-btn-wrapper');
                const firstDiv = form.querySelector('div:first-child');
                const lastWrapper = firstDiv?.querySelector('.wrapper:last-child');

                if (buttonWrapper && lastWrapper && firstDiv) {
                    // Create a container for input and buttons
                    const container = document.createElement('div');
                    container.className = 'input-button-container';
                    container.style.cssText = `
                        display: flex;
                        align-items: flex-end;
                        gap: 12px;
                        flex-wrap: wrap;
                        margin-top: 8px;
                    `;

                    // Style the last wrapper to flex grow
                    (lastWrapper as HTMLElement).style.flex = '1';
                    (lastWrapper as HTMLElement).style.minWidth = '200px';

                    // Style the button wrapper
                    (buttonWrapper as HTMLElement).style.display = 'flex';
                    (buttonWrapper as HTMLElement).style.gap = '10px';
                    (buttonWrapper as HTMLElement).style.flexShrink = '0';

                    // Save the parent and next sibling before moving lastWrapper
                    const parent = lastWrapper.parentNode;
                    const nextSibling = lastWrapper.nextSibling;

                    // Move the original elements into container (preserves event listeners)
                    container.appendChild(lastWrapper);
                    container.appendChild(buttonWrapper);

                    // Insert the container where the lastWrapper was originally
                    if (parent) {
                        if (nextSibling) {
                            parent.insertBefore(container, nextSibling);
                        } else {
                            parent.appendChild(container);
                        }
                    }

                    form.setAttribute('data-buttons-fixed', 'true');
                }
            });
        };

        // Use MutationObserver to fix buttons when modal opens
        const observer = new MutationObserver(() => {
            fixButtonAlignment();
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        // Run immediately and with delays
        setTimeout(fixButtonAlignment, 100);
        setTimeout(fixButtonAlignment, 300);
        setTimeout(fixButtonAlignment, 500);

        return () => {
            observer.disconnect();
        };
    }, []);

    // Add banner to Bearer token authorization section only
    useEffect(() => {
        const checkAndAddBanner = () => {
            // Try multiple approaches to find the Bearer section

            // Approach 1: Look for the modal dialog
            const modalDialog = document.querySelector('.dialog-ux');
            if (modalDialog) {
                // Find all sections within the modal
                const sections = modalDialog.querySelectorAll('section');

                sections.forEach(section => {
                    const textContent = section.textContent || '';

                    // Check if this is the Bearer section and doesn't already have the banner
                    if (textContent.includes('Bearer') &&
                        textContent.includes('http') &&
                        !textContent.includes('ApiGateway') &&
                        !section.querySelector('.auth-info-banner')) {

                        // Create the banner element
                        const banner = document.createElement('div');
                        banner.className = 'auth-info-banner';
                        banner.style.cssText = `
                            background-color: #e3f2fd;
                            color: #1565c0;
                            padding: 8px 12px;
                            margin: 8px 0;
                            border-radius: 3px;
                            font-size: 12px;
                            line-height: 1.3;
                            border-left: 3px solid #1976d2;
                        `;
                        banner.innerHTML = '<strong>ℹ️</strong> Bearer token is optional - your logged-in user token will be used automatically if not provided';

                        // Find the heading "Bearer (http, Bearer)"
                        const heading = section.querySelector('h4');
                        if (heading) {
                            // Insert the banner right after the heading
                            heading.parentNode?.insertBefore(banner, heading.nextSibling);
                        } else {
                            // Fallback: insert at the beginning
                            section.insertBefore(banner, section.firstChild);
                        }

                        console.log('Bearer banner added successfully');
                    }
                });
            }

            // Approach 2: Fallback to looking for auth-container
            if (!document.querySelector('.auth-info-banner')) {
                const authContainer = document.querySelector('.auth-container');
                if (authContainer) {
                    const allDivs = authContainer.querySelectorAll('div');

                    allDivs.forEach(div => {
                        const textContent = div.textContent || '';

                        if (textContent.includes('Bearer') &&
                            textContent.includes('http') &&
                            !textContent.includes('ApiGateway') &&
                            !div.querySelector('.auth-info-banner')) {

                            // Create the banner
                            const banner = document.createElement('div');
                            banner.className = 'auth-info-banner';
                            banner.style.cssText = `
                                background-color: #e3f2fd;
                                color: #1565c0;
                                padding: 8px 12px;
                                margin: 8px 0;
                                border-radius: 3px;
                                font-size: 12px;
                                line-height: 1.3;
                                border-left: 3px solid #1976d2;
                            `;
                            banner.innerHTML = '<strong>ℹ️</strong> Bearer token is optional - your logged-in user token will be used automatically if not provided';

                            // Find the heading within this div
                            const heading = div.querySelector('h4');
                            if (heading) {
                                // Insert right after the heading
                                heading.parentNode?.insertBefore(banner, heading.nextSibling);
                            } else {
                                // Insert at the beginning of the div
                                div.insertBefore(banner, div.firstChild);
                            }

                            console.log('Bearer banner added via fallback');
                        }
                    });
                }
            }
        };

        // Use MutationObserver to detect when the authorization modal is opened
        const observer = new MutationObserver(() => {
            // Check whenever the DOM changes
            checkAndAddBanner();
        });

        // Start observing with a broader scope
        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        // Check multiple times with increasing delays to catch the modal
        setTimeout(checkAndAddBanner, 100);
        setTimeout(checkAndAddBanner, 300);
        setTimeout(checkAndAddBanner, 500);
        setTimeout(checkAndAddBanner, 1000);

        return () => {
            observer.disconnect();
        };
    }, []);

    return (
        <SwaggerUI
            {...props}
            spec={modifiedSpec}
            url={undefined} // Ensure we're not loading from a URL
            requestInterceptor={async req => {
                // Don't modify the URL - let the fetch interceptor handle proxying
                // But add the Authorization header so it appears in the curl command

                req.headers = req.headers || {};

                // Check if user has provided their own Bearer token via Swagger UI auth dialog
                const existingAuth = req.headers['Authorization'] || req.headers['authorization'];
                const hasUserProvidedToken = existingAuth &&
                    typeof existingAuth === 'string' &&
                    existingAuth.startsWith('Bearer ') &&
                    existingAuth.length > 10; // "Bearer " + at least a few chars

                // Only get automatic token if user hasn't provided one
                if (!hasUserProvidedToken) {
                    try {
                        const accessToken = await platformAuth.getAccessToken([
                            'openid',
                            'profile',
                            'email',
                        ]);
                        if (accessToken) {
                            req.headers['Authorization'] = `Bearer ${accessToken}`;
                        }
                    } catch (e) {
                        console.error('Failed to get access token for curl', e);
                    }
                }

                // Let any original requestInterceptor run if present
                if (props.requestInterceptor) {
                    return (await props.requestInterceptor(req)) ?? req;
                }

                return req;
            }}
        />
    );
}


export const apis: AnyApiFactory[] = [
    createApiFactory({
        api: platformAuthApiRef,
        deps: {
            discoveryApi: discoveryApiRef,
            oauthRequestApi: oauthRequestApiRef,
            configApi: configApiRef,
        },
        factory: ({ discoveryApi, oauthRequestApi, configApi }) => {
            // Create the underlying OAuth2 instance for OIDC fallback (direct access / dev)
            const oauth2 = OAuth2.create({
                configApi,
                discoveryApi,
                oauthRequestApi,
                provider: {
                    id: 'oidc',
                    title: 'Platform Login',
                    icon: () => null,
                },
                environment: configApi.getOptionalString('auth.environment'),
                defaultScopes: ['openid', 'profile', 'email'],
            });

            // Proxy: delegates all methods to oauth2, but overrides getAccessToken
            // to return the platform JWT when in iframe flow.
            // When the token expires, uses oidc-client-ts for silent refresh.
            // Using a Proxy because OAuth2 methods live on the prototype and
            // won't survive object spread.
            return new Proxy(oauth2, {
                get(target, prop, receiver) {
                    if (prop === 'getAccessToken') {
                        return async (scopes?: string[]) => {
                            // Platform token flow (iframe)
                            if (_platformToken) {
                                if (!isTokenExpired()) {
                                    return _platformToken;
                                }
                                // Token expired — re-trigger OIDC redirect to get a fresh token.
                                // The user has an active platform session so the redirect
                                // is transparent (prompt=none, no login form).
                                console.log('[Platform Auth] Token expired — triggering re-auth redirect');
                                triggerReauthRedirect(configApi);
                                return _platformToken;
                            }
                            // OAuth2 fallback (local dev) — Backstage OAuth2 handles its own refresh
                            try {
                                const token = await target.getAccessToken(scopes);
                                if (token) {
                                    const payload = JSON.parse(atob(token.split('.')[1]));
                                    const exp = new Date((payload.exp || 0) * 1000);
                                    const remaining = Math.round((exp.getTime() - Date.now()) / 1000);
                                    console.log(`[Platform Auth] OAuth2 token OK. Expires: ${exp.toISOString()} (${remaining}s remaining)`);
                                }
                                return token;
                            } catch (e) {
                                console.error('[Platform Auth] OAuth2 getAccessToken failed:', e);
                                throw e;
                            }
                        };
                    }
                    const value = Reflect.get(target, prop, receiver);
                    if (typeof value === 'function') {
                        return value.bind(target);
                    }
                    return value;
                },
            }) as OAuthApi & ProfileInfoApi & BackstageIdentityApi & SessionApi;
        },
    }),
    // SCM integrations
    createApiFactory({
        api: scmIntegrationsApiRef,
        deps: { configApi: configApiRef },
        factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
    }),
    ScmAuth.createDefaultApiFactory(),
    createApiFactory({
        api: graphQlBrowseApiRef,
        deps: {
            catalogApi: catalogApiRef,
            discoveryApi: discoveryApiRef,
            platformAuth: platformAuthApiRef,
        },
        factory: ({ catalogApi, discoveryApi, platformAuth }) => ({
            async getEndpoints(): Promise<GraphQLEndpoint[]> {
                const { items } = await catalogApi.getEntities({
                    filter: [{ kind: 'API', 'spec.type': 'graphql' }],
                });
                return items.map(entity => {
                    const endpointPath =
                        entity.metadata.annotations?.['causeway.com/graphql-endpoint'] ?? '/graphql';
                    const id = `${entity.metadata.namespace ?? 'default'}--${entity.metadata.name}`;
                    const title = (entity.metadata.title ?? entity.metadata.name) as string;
                    return {
                        id,
                        title,
                        fetcher: async (params: any) => {
                            const isFullUrl = endpointPath.startsWith('http://') || endpointPath.startsWith('https://');
                            let url: string;
                            let headers: Record<string, string> = { 'Content-Type': 'application/json' };
                            if (isFullUrl) {
                                // Direct request — used for external/public endpoints
                                url = endpointPath;
                            } else {
                                // Route through api-proxy, inject auth
                                const proxyBase = await discoveryApi.getBaseUrl('api-proxy');
                                url = `${proxyBase}${endpointPath}`;
                                try {
                                    const token = await platformAuth.getAccessToken(['openid', 'profile', 'email']);
                                    if (token) headers['Authorization'] = `Bearer ${token}`;
                                } catch {
                                    // continue without token
                                }
                            }
                            return fetch(url, {
                                method: 'POST',
                                headers,
                                body: JSON.stringify(params),
                            }).then(r => r.json());
                        },
                    };
                });
            },
        }),
    }),
    createApiFactory({
        api: apiDocsConfigRef,
        deps: {},
        factory: (): ApiDocsConfig => {
            const widgets: ApiDefinitionWidget[] = defaultDefinitionWidgets().map(
                widget => {
                    if (widget.type === 'openapi') {
                        return {
                            ...widget,
                            // `definition` here is actually the OpenAPI spec object
                            component: definition => <ProxiedSwaggerUi spec={definition} />,
                        };
                    }
                    return widget;
                },
            );

            return {
                getApiDefinitionWidget(apiEntity: ApiEntity) {
                    return widgets.find(w => w.type === apiEntity.spec.type);
                },
            };
        },
    }),
];
